import React, { useState, useEffect } from 'react';
import { Package, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { API_CONFIG } from './config/apiConfig';

const CHANGE_TYPES = {
    new:         { label: 'Nouveau',      color: '#10b981', bg: 'rgba(16,185,129,0.12)',  border: 'rgba(16,185,129,0.3)'  },
    fix:         { label: 'Correctif',    color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',  border: 'rgba(245,158,11,0.3)'  },
    improvement: { label: 'Amélioration', color: '#3b82f6', bg: 'rgba(59,130,246,0.12)',  border: 'rgba(59,130,246,0.3)'  },
    other:       { label: 'Autre',        color: '#E8B75A', bg: 'rgba(232,183,90,0.12)',  border: 'rgba(232,183,90,0.3)'  },
};

const normalizeChange = (c) => typeof c === 'string' ? { type: 'other', text: c } : c;

const VersionCard = ({ changelog, isLatest, defaultOpen }) => {
    const [open, setOpen] = useState(defaultOpen);
    const changes = changelog.changes.map(normalizeChange);

    const countByType = changes.reduce((acc, c) => {
        acc[c.type] = (acc[c.type] || 0) + 1;
        return acc;
    }, {});

    return (
        <div style={{
            background: 'var(--glass)',
            border: `1px solid ${isLatest ? 'rgba(232,183,90,0.4)' : 'var(--glass-border)'}`,
            borderRadius: 'var(--r-xl)',
            overflow: 'hidden',
            transition: 'border-color 200ms ease',
        }}>
            {/* Card header — cliquable */}
            <button
                onClick={() => setOpen(o => !o)}
                style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '18px 20px',
                    background: isLatest ? 'linear-gradient(135deg, rgba(232,183,90,0.12), rgba(196,70,47,0.06))' : 'none',
                    border: 'none', cursor: 'pointer', gap: 12,
                    transition: 'background 150ms ease',
                }}
                onMouseEnter={e => { if (!isLatest) e.currentTarget.style.background = 'var(--glass-hover)'; }}
                onMouseLeave={e => { if (!isLatest) e.currentTarget.style.background = 'none'; }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                    {/* Badge version */}
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '5px 12px', borderRadius: 'var(--r-full)',
                        background: isLatest
                            ? 'linear-gradient(135deg, var(--accent), var(--pink))'
                            : 'var(--glass-active)',
                        color: isLatest ? '#fff' : 'var(--text-2)',
                        fontWeight: 700, fontSize: '0.875rem', flexShrink: 0,
                    }}>
                        <Package style={{ width: 14, height: 14 }} />
                        v{changelog.version}
                    </span>

                    {isLatest && (
                        <span style={{
                            padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700,
                            color: '#c4b5fd', background: 'rgba(232,183,90,0.2)', border: '1px solid rgba(232,183,90,0.35)',
                            flexShrink: 0,
                        }}>
                            Actuelle
                        </span>
                    )}

                    <div style={{ minWidth: 0 }}>
                        <p style={{
                            color: 'var(--text-1)', fontWeight: 600, fontSize: '0.9375rem',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            textAlign: 'left',
                        }}>
                            {changelog.title}
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--text-4)', fontSize: '0.8125rem' }}>
                                <Calendar style={{ width: 12, height: 12 }} />
                                {new Date(changelog.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </span>
                            {/* Compteurs de types */}
                            {Object.entries(countByType).map(([type, count]) => {
                                const t = CHANGE_TYPES[type] || CHANGE_TYPES.other;
                                return (
                                    <span key={type} style={{
                                        padding: '1px 6px', borderRadius: 99, fontSize: 10, fontWeight: 700,
                                        color: t.color, background: t.bg, border: `1px solid ${t.border}`,
                                    }}>
                                        {count} {t.label.toLowerCase()}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div style={{ color: 'var(--text-4)', flexShrink: 0 }}>
                    {open
                        ? <ChevronUp style={{ width: 18, height: 18 }} />
                        : <ChevronDown style={{ width: 18, height: 18 }} />
                    }
                </div>
            </button>

            {/* Changements */}
            {open && (
                <div style={{
                    padding: '0 20px 18px',
                    borderTop: '1px solid var(--glass-border)',
                    display: 'flex', flexDirection: 'column', gap: 6,
                    paddingTop: 14,
                }}>
                    {changes.map((c, i) => {
                        const typeInfo = CHANGE_TYPES[c.type] || CHANGE_TYPES.other;
                        return (
                            <div key={i} style={{
                                display: 'flex', alignItems: 'flex-start', gap: 10,
                                padding: '10px 12px',
                                background: 'var(--glass)',
                                border: '1px solid var(--glass-border)',
                                borderRadius: 'var(--r-md)',
                                animation: 'fadeUp 0.2s ease-out',
                                animationDelay: `${i * 0.04}s`,
                                animationFillMode: 'both',
                            }}>
                                <span style={{
                                    padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700,
                                    color: typeInfo.color, background: typeInfo.bg,
                                    border: `1px solid ${typeInfo.border}`,
                                    flexShrink: 0, marginTop: 1, whiteSpace: 'nowrap',
                                }}>
                                    {typeInfo.label}
                                </span>
                                <p style={{ color: 'var(--text-2)', lineHeight: 1.6, fontSize: '0.9rem' }}>{c.text}</p>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const PatchNotesPage = () => {
    const [changelogs, setChangelogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                const response = await fetch(`${API_CONFIG.NEW_API_BASE}/changelogs`, {
                    headers: {
                        'Accept': 'application/json',
                        'X-Admin-Token': localStorage.getItem('cinestream_token') || '',
                    }
                });
                const data = await response.json();
                if (data.success) setChangelogs(data.changelogs || []);
            } catch (err) {
                console.error('Erreur chargement patch notes:', err);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
                <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    border: '2px solid var(--glass-border)',
                    borderTopColor: 'var(--accent)',
                    animation: 'spinSlow 0.8s linear infinite',
                }} />
            </div>
        );
    }

    if (changelogs.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-4)' }}>
                <Package style={{ width: 40, height: 40, margin: '0 auto 12px', opacity: 0.4 }} />
                <p style={{ fontSize: '0.9375rem' }}>Aucune note de version pour l'instant.</p>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* Légende des types */}
            <div style={{
                display: 'flex', flexWrap: 'wrap', gap: 6,
                padding: '10px 14px',
                background: 'var(--glass)',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--r-lg)',
                marginBottom: 4,
            }}>
                {Object.entries(CHANGE_TYPES).map(([key, t]) => (
                    <span key={key} style={{
                        padding: '2px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700,
                        color: t.color, background: t.bg, border: `1px solid ${t.border}`,
                    }}>
                        {t.label}
                    </span>
                ))}
            </div>

            {changelogs.map((cl, index) => (
                <VersionCard
                    key={cl.id}
                    changelog={cl}
                    isLatest={index === 0}
                    defaultOpen={index === 0}
                />
            ))}
        </div>
    );
};

export default PatchNotesPage;
